import { useMemo, useContext } from "react";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";

import { Link, useSearchParams } from "react-router-dom";

import { 
  Table, 
  Pagination,
  StatusBadge
} from "@evos/components";
import Header from "components/Header";
// import RuntimeTypeCheck from "components/RuntimeTypeCheck";
import ChargerFilterBar from "components/ChargerFilterBar";

import { useWindowSize, usePaginationHandlers } from "@evos/hooks";
import { AuthContext } from "lib/context/auth";
import { isEmpty } from "lib/functions/general";
import { lastCommsFilterToTimestamp, lastCommsTimestampToFilter } from "lib/functions/charger";

import { CHARGERS } from "./gql";

import {
  ColumnDef
} from "@tanstack/react-table";

import { 
  formatDistanceToNowStrict,
} from "date-fns";

import {
  useQuery,
  NetworkStatus
} from "@apollo/client";

import type { ChargerNode, ChargersConnection, WhereArgument } from "types";

function useAuth() {
  return useContext(AuthContext);
}

interface TableData  {
  pinned?: boolean,
  serial: string,
  lastWireguardHeartbeat: string,
  allowedIps: string,
  location: string,
  mode: string,
  salesforceStatus: string,
  ocppStatus: string
  ocppServer: string,
}

type ChargerFilters = {
  ocpp_status: string[]
  installation: string[]
  last_comms: string[]
  search: string[]
  firmware_version: string[]
}

function constructWhereArgument(filters: ChargerFilters): WhereArgument["where"] {
  return Object.values(filters).every((filter) => filter.length === 0) 
    ? null
    : {
      ...(filters.ocpp_status.length > 0 && { ocpp_status: {in: filters.ocpp_status} }),
      ...(filters.installation.length === 1
        && { installation_ref: filters.installation[0] === "Installed"
          ? {ne: null}
          : {eq: null}
        }),
      ...(filters?.last_comms?.length ?? 0) > 0
        && { last_comms: filters.last_comms[0]?.includes("<")
          ? {gte: filters.last_comms[0]?.substring(1)}
          : {lte: filters.last_comms[0]?.substring(1)},
        },
      ...(filters?.firmware_version?.[0]?.length > 0
        && { firmware_version: { eq: filters.firmware_version[0] } }),
      ...(filters?.search?.[0]?.length > 0
        && { _or: [
          {
            serial: {like: filters.search[0]}
          },
          {
            firmware_version: {like: filters.search[0]}
          },
          {
            street_address: {like: filters.search[0]}
          },
          {
            suburb: {like: filters.search[0]}
          },
          {
            city: {like: filters.search[0]}
          },
          {
            province: {like: filters.search[0]}
          },
          {
            country: {like: filters.search[0]}
          }
        ]  
        }
      )
    };
}

export default function ChargerList() {
  const auth = useAuth();
  const darkMode = auth?.darkMode ?? false;
  const windowWidth = useWindowSize().width ?? window.innerWidth;

  const [ searchParams, setSearchParams ] = useSearchParams();
  const showCountSearchParam = useMemo(() => searchParams.get("show") ?? "16", [searchParams]);
  const initialShowCount = Number.parseInt(showCountSearchParam, 10) || 16;

  const filterSearchParams = {
    ocpp_status: searchParams.get("ocpp_status")?.split(", ") ?? [],
    installation: searchParams.get("installation")?.split(", ") ?? [],
    last_comms: searchParams.get("last_comms")?.split(", ") ?? [],
    search: searchParams.get("search")?.split(", ") ?? [],
    firmware_version: searchParams.get("firmware_version")?.split(", ") ?? [],
  };

  const chargersWhereArgument = constructWhereArgument(filterSearchParams);

  const { 
    data: chargersData, 
    networkStatus: chargersQueryNetworkStatus,
    error: chargersError, 
    fetchMore: fetchMoreChargers
  } = useQuery<ChargersConnection>(
    CHARGERS,
    { 
      variables: {
        chargers_n: initialShowCount, 
        after: null,
        chargers_where: isEmpty(chargersWhereArgument) ? null : chargersWhereArgument
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all"
    }
  );

  const chargersInitialLoading = chargersQueryNetworkStatus === NetworkStatus.loading;
  const chargersVariableChangeLoading = chargersQueryNetworkStatus === NetworkStatus.setVariables;
  const chargersFetchMoreLoading = chargersQueryNetworkStatus === NetworkStatus.fetchMore;

  const chargersPageInfo = chargersData?.chargers?.pageInfo;
  const totalCount = chargersData?.chargers?.totalCount;
  const loadedChargerCount = chargersData?.chargers?.edges?.length ?? 0;

  const { 
    paginationState,
    handleNextPage,
    handlePrevPage,
    handlePageSize,
    setPaginationState,
  } = usePaginationHandlers({
    pageInfoEndCursor: chargersPageInfo?.endCursor,
    listOfCursors: chargersData?.chargers?.edges?.map((c) => c.cursor),
    fetchMore: fetchMoreChargers,
    refetchKeyNames: ["chargers_n", "after", "chargers_where"],
    initialWhereArgument: chargersWhereArgument,
    initialShowCount,
    setSearchParams
  });

  const paginationTotalCount = useMemo(() => {
    if (totalCount == null) {
      return undefined;
    }

    return chargersPageInfo?.hasNextPage === true && totalCount <= loadedChargerCount
      ? undefined
      : totalCount;
  }, [chargersPageInfo?.hasNextPage, loadedChargerCount, totalCount]);

  const paginatedChargers = useMemo(() => (
    (chargersData?.chargers?.edges?.map((edge) => edge.node).slice(
      (paginationState.pageSize * paginationState.pageNumber - paginationState.pageSize), (paginationState.pageSize * paginationState.pageNumber)))
  ), [chargersData?.chargers?.edges, paginationState.pageNumber, paginationState.pageSize]);

  const chargerTableData = paginatedChargers?.map((d) => ({
    serial: d?.serial,
    location: (d?.salesforce_details?.street_address != null && d?.salesforce_details?.suburb != null)
      ? `${d.salesforce_details.street_address}, ${d.salesforce_details.suburb ?? ""}`
      : "Unknown",
    lastComms: (d?.salesforce_details?.last_comms != null) 
      ? `${formatDistanceToNowStrict(new Date(d?.salesforce_details?.last_comms))} ago`
      : "Never",
    allowedIps: (d?.salesforce_details?.vpn_ip != null && d.salesforce_details.vpn_ip !== "")
      ? d.salesforce_details.vpn_ip
      : (d?.vpn_status?.connections?.length > 1
        ? d?.vpn_status?.connections?.sort((a, b) => (a?.receive_timestamp ?? 0) > (b?.receive_timestamp ?? 0) ? -1 : 1)?.[0]?.allowed_ips ?? "Unknown"
        : d?.vpn_status?.connections?.[0]?.allowed_ips ?? "Unknown"),
    ocppStatus: d?.salesforce_details?.ocpp_status ?? "Unknown",
    ocppServer: d?.salesforce_details?.ocpp_server ?? "Unknown",
    firmwareVersion: d?.salesforce_details?.firmware_version ?? "Unknown",
  }));

  const chargerColumns = useMemo<Array<ColumnDef<TableData>>>(
    () => [
      {
        header: "Serial",
        accessorKey: "serial",
        cell: cell => <Link to={`/c/${cell.row.original.serial}`} style={{color: darkMode ? "#1E90FF" : "blue"}}>{cell.row.original.serial}</Link>
      },
      {
        header: "Location",
        accessorKey: "location",
      },
      {
        header: "Last Comms",
        accessorKey: "lastComms"
      },
      {
        header: "IP Address",
        accessorKey: "allowedIps",
        cell: cell => cell.row.original.allowedIps?.includes("/")
          ? cell.row.original.allowedIps?.split("/")?.[0]
          : cell.row.original.allowedIps
      },
      {
        header: "OCPP Status",
        accessorKey: "ocppStatus",
        cell: cell => <><StatusBadge status={cell.row.original.ocppStatus as ChargerNode["salesforce_details"]["ocpp_status"]} showStandardName /> {cell.row.original.ocppStatus}</>
      },
      {
        header: "OCPP Server",
        accessorKey: "ocppServer",
        cell: cell => cell.row.original.ocppServer?.includes("ws")
          ? cell.row.original.ocppServer?.split("ws://")[1]
          : cell.row.original.ocppServer
      },
      {
        header: "Firmware Version",
        accessorKey: "firmwareVersion"
      },
    ],
    [darkMode]
  );

  const decodeQueryParams = <T,>(fieldNames: string[], initialObj: T) => {
    return fieldNames.reduce((acc, key) => 
      ({...acc, [key]: key === "search" 
        ? [searchParams.get("search") ?? ""]
        : key === "last_comms" 
          ? searchParams.get(key) != null
            ? [lastCommsTimestampToFilter(searchParams.get(key) as string)]
            : []
          : searchParams.get(key)?.split(", ") ?? []
      }), initialObj);
  };

  const setFilters = (filters: Pick<ChargerFilters, "ocpp_status" | "installation" | "last_comms" | "search"> & Partial<Pick<ChargerFilters, "firmware_version">>) => {
    const normalizedFilters: ChargerFilters = {
      ocpp_status: filters.ocpp_status,
      installation: filters.installation,
      last_comms: filters.last_comms,
      search: filters.search,
      firmware_version: filters.firmware_version ?? [],
    };
    // No refetch required, setting the search params causes the chargers query
    // to re-render due to the 'cache-and-network' fetch policy
    const whereArgument = constructWhereArgument(normalizedFilters);

    if (whereArgument == null || isEmpty(whereArgument)) {
      setPaginationState((prev) => ({
        pageSize: prev.pageSize,
        pageNumber: 1, 
        where: null
      }));
      
      setSearchParams();
      
    } else {
      const encodedFilters = { 
        ocpp_status: normalizedFilters.ocpp_status.join(", "),
        installation: normalizedFilters.installation.join(", "),
        last_comms: lastCommsFilterToTimestamp(normalizedFilters.last_comms[0] ?? ""),
        search: normalizedFilters.search[0],
        firmware_version: normalizedFilters.firmware_version[0] ?? "",
      };
      setPaginationState((prev) => ({
        pageSize: prev.pageSize,
        pageNumber: 1, 
        where: whereArgument
      }));

      setSearchParams((prevParams) => {
        Object.entries(encodedFilters).forEach(([key, value]) => {
          if (value.length > 0) {
            prevParams.set(key, value);
          } else {
            prevParams.delete(key);
          }
        });
        return prevParams;
      });
    }
  };
  
  return (
    <div id="hardware-dashboard">
      <Header />
      <Container>
        <Row className="mt-3 pb-5">
          <Col xs={12}>
            <ChargerFilterBar
              darkMode={darkMode}
              windowWidth={windowWidth}
              loading={chargersInitialLoading || chargersVariableChangeLoading || chargersFetchMoreLoading}
              decodedFilters={decodeQueryParams<ChargerFilters>(
                ["ocpp_status", "installation", "last_comms", "search", "firmware_version"], 
                {ocpp_status: [], installation: [], last_comms: [], search: [], firmware_version: []}
              )}
              setFilters={setFilters}
            />
          </Col>
          <Col xs={12}>
            <Card className={`pb-3 mt-3 ${darkMode ? "bg-dark" : ""}`}>
              <div className="mt-3 mx-3">
                {/* { !chargersInitialLoading 
                  && <RuntimeTypeCheck data={chargersData}/>
                } */}
              </div>
              <Table
                className="mt-0"
                error={chargersError != null && chargerTableData?.length === 0}
                isLoading={chargersInitialLoading}
                columns={chargerColumns}
                darkMode={darkMode!}
                data={chargerTableData ?? []}
                noDataText="You have no installed chargers"
                pageInfo={chargersPageInfo}
                totalCount={chargersData?.chargers?.totalCount}
                breakpointResponsiveAt="xl"
              />
              <div className="px-3">
                <Pagination
                  pageLoading={chargersInitialLoading || chargersFetchMoreLoading}
                  paginationState={paginationState}
                  handleNextPage={handleNextPage}
                  handlePrevPage={handlePrevPage}
                  handlePageSize={handlePageSize}
                  hasNextPage={chargersPageInfo?.hasNextPage}
                  totalCount={paginationTotalCount}
                  enableInfinitePagination={paginationTotalCount == null}
                /> 
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}